import React, { useCallback, useEffect, useState } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import { Layout } from "../components/Layout";
import { UserInfo } from "../models/user-info";
import exportService, { ExportType } from "../services/export-service";
import { Toast } from "../services/toast-service";

const EXPORT_TYPES: ExportType[] = ["notifications", "results", "activity"];
const POLLING_INTERVAL = 10000;

type ExportScreenProps = {
  userInfo: UserInfo;
};

const ExportScreen: React.FC<ExportScreenProps> = (props) => {
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [isListLoading, setIsListLoading] = useState(false);
  const [exportRecords, setExportRecords] = useState<{ [key: string]: any }>(
    {},
  );

  const fetchExportRecords = async () => {
    try {
      setIsListLoading(true);
      const records = await exportService.getExportList();
      // convert to map
      const recordsMap = Object.fromEntries(
        records.map((record) => [record.ExportType, record]),
      );
      setExportRecords(recordsMap);
    } catch (err) {
      console.error("Error fetching export list:", err);
      Toast("Error", "Failed to fetch export records.", "danger");
    } finally {
      setIsListLoading(false);
    }
  };

  const checkIfAnythingInProgress = useCallback(() => {
    const hasInProgressExports = Object.values(exportRecords).some(
      (record) =>
        record?.ExportStatus === "STARTING" ||
        record?.ExportStatus === "IN_PROGRESS",
    );
    return hasInProgressExports;
  }, [exportRecords]);

  useEffect(() => {
    fetchExportRecords();
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (checkIfAnythingInProgress()) {
      intervalId = setInterval(fetchExportRecords, POLLING_INTERVAL);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [checkIfAnythingInProgress]);

  const handleGenerateExport = async (exportType: ExportType) => {
    if (!loading[exportType]) {
      try {
        setLoading((prev) => ({ ...prev, [exportType]: true }));
        const exportRecord = await exportService.generateExport(exportType);
        Toast("Request Sent", exportRecord.ExportStatus, "success");
        await fetchExportRecords();
      } catch (err: unknown) {
        console.error("Error generating export:", err);
        let errorMessage = "Something went wrong generating the export.";

        if (exportService.isHttpError(err)) {
          const httpError: any = err;
          if (httpError.response?.data?.message) {
            errorMessage = httpError.response.data.message;
          } else if (httpError.message) {
            errorMessage = httpError.message;
          }
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }
        Toast("Error", errorMessage, "danger");
      } finally {
        setLoading((prev) => ({ ...prev, [exportType]: false }));
      }
    }
  };

  const handleDownloadExport = async (exportType: ExportType) => {
    try {
      const downloadUrl = await exportService.getExportDownload(exportType);
      window.open(downloadUrl, "_blank");
    } catch (error) {
      console.error("Error downloading export:", error);
      Toast("Error", "Failed to download export file.", "danger");
    }
  };

  const getExportStatus = (exportType: ExportType) => {
    const record = exportRecords[exportType];
    if (!record) return null;

    const lastUpdated = new Date(record.LastUpdated);
    const timeStr = lastUpdated.toLocaleTimeString();
    const dateStr = lastUpdated.toLocaleDateString();

    return `${record.ExportStatus} (${timeStr} ${dateStr})${
      record.LastMessage ? ` ${record.LastMessage}` : ""
    }`;
  };

  return (
    <Layout userInfo={props.userInfo} loading={isListLoading}>
      <div className="flex flex-col pt-8 px-20">
        <h1 className="text-lg pb-2">Export CSV</h1>
        {EXPORT_TYPES.map((exportType) => {
          const exportRecord = exportRecords[exportType];
          const status = getExportStatus(exportType);
          return (
            <div key={exportType} className="mb-4">
              <div className="flex flex-row items-center">
                <div className="w-64">
                  <p className="text-sm capitalize">{exportType}</p>
                </div>

                <div className="flex items-center h-10">
                  {loading[exportType] ? (
                    <ClipLoader size={50} />
                  ) : (
                    <>
                      <button
                        onClick={() => handleGenerateExport(exportType)}
                        className="text-white text-xs rounded px-5 py-2 cursor-pointer bg-[#e64626] mr-4"
                      >
                        Generate
                      </button>

                      {exportRecord?.ExportStatus === "COMPLETED_SUCCESS" && (
                        <button
                          onClick={() => handleDownloadExport(exportType)}
                          className="text-white text-xs rounded px-5 py-2 cursor-pointer bg-green-600"
                        >
                          Download
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {status && (
                <div className="mt-1">
                  <p className="text-xs text-gray-500">{status}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Layout>
  );
};

export default ExportScreen;
